import React, { useMemo } from 'react';
import styled from 'styled-components';

import { LayoutColors } from '@src/core/theme';
import OrderStatusDisplay from '@src/components/ui/OrderStatusDisplay';
import useTranslate from '@src/i18n/useTranslate';
import { Order, OrderStatus } from '@src/core/buyerApiClient';
import { formatCents, formatDateTime } from '@src/common';
import {
  BodyRegular,
  BodyRegularMedium,
  BodyRegularSemiBold,
  BodySmall,
  BodySmallMedium,
} from '@src/fonts/typography/Typography';
import Reservations from './Reservations';
import { device } from '@src/common/breakpoints';
import { orderSaleItems } from '@src/common/order';
import { track } from '@src/services/analytics';

type Props = {
  order: Order;
  showReservationsPreview: boolean;
  onOrderSelection: (order: Order) => void;
  onOrderCheckout: (order: Order) => void;
};

const OrderCard = (props: Props) => {
  const { order, showReservationsPreview, onOrderSelection, onOrderCheckout } = props;
  const { i18nElement, i18n } = useTranslate();

  const handleViewSaleClick = () => {
    track('saleViewButtonClick', { orderId: order.id });

    onOrderSelection(order);
  };

  const saleItems = useMemo(() => orderSaleItems(order), [order]);

  return (
    <Container>
      <Header>
        <h3>
          {i18nElement('account_sellNumber', {
            orderCode: order.code,
          })}
        </h3>
        <OrderStatusDisplay status={order.status} />
      </Header>

      <HeaderInfo>
        <span>{formatDateTime(order.createdAt)}</span>
        {!showReservationsPreview ? (
          <span>
            {i18nElement('account_itemsQuantity', {
              quantity: saleItems.length.toString(),
            })}
          </span>
        ) : null}
        <p>{formatCents(order.finalAmountCents)}</p>
      </HeaderInfo>
      {showReservationsPreview ? <Reservations saleItems={saleItems} /> : null}

      <Actions>
        <ButtonView onClick={handleViewSaleClick}>{i18n('account_view')}</ButtonView>

        {order.status === OrderStatus.Created ? (
          <ButtonView variant='filled' onClick={() => onOrderCheckout(order)}>
            {i18n('account_sale_checkout')}
          </ButtonView>
        ) : null}
      </Actions>
    </Container>
  );
};

export default OrderCard;

const Container = styled.article`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 8px;
  gap: 8px;

  width: 100%;

  background: ${LayoutColors.whiteLight};
  border-radius: 4px;

  ${device.smallTablet} {
    padding: 16px;
  }
`;
const Header = styled.header`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;

  h3 {
    ${BodyRegularMedium};
    color: ${LayoutColors.blackMain};
  }

  ${device.smallTablet} {
    flex-direction: row;
    align-items: baseline;
    justify-content: space-between;

    h3 {
      ${BodyRegular};
      color: ${LayoutColors.blackDark};
    }
  }
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 8px;

  span {
    ${BodySmall};
    color: ${LayoutColors.blackMain};
  }

  p {
    ${BodySmallMedium};
    color: ${LayoutColors.blackMain};
  }
`;

const ButtonView = styled.button<{ variant?: 'outlined' | 'filled' }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  flex-grow: 0;
  width: 100%;

  background: ${({ variant = 'outlined' }) =>
    variant === 'outlined' ? LayoutColors.whiteLight : LayoutColors.justBlue};
  border: 1px solid ${LayoutColors.justBlue};
  border-radius: 4px;

  ${BodyRegularSemiBold};
  color: ${({ variant = 'outlined' }) => (variant === 'outlined' ? LayoutColors.justBlue : LayoutColors.whiteLight)};
  padding: 10px 12px;
  transition: all 0.1s linear;

  &:hover {
    background: ${LayoutColors.justBlue};
    color: ${LayoutColors.whiteLight};
  }

  ${device.smallTablet} {
    margin-top: 8px;
  }
`;

export const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-self: stretch;
  flex-grow: 1;

  ${device.smallTablet} {
    flex-direction: row;
  }
`;
